<div class="p-6 md:p-8 space-y-8 min-h-screen bg-base-200/30 animate-in fade-in duration-700">
  
  <!-- Dashboard Header & Global Filters -->
  <header class="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2">
    <div class="flex items-center gap-4">
      <div class="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shadow-inner">
        <i class="fa-solid fa-chart-pie text-2xl"></i>
      </div>
      <div>
        <h1 class="text-3xl font-black tracking-tight text-base-content">Business Insights</h1>
        <p class="text-sm font-medium text-base-content/50 mt-1 flex items-center gap-2">
          <span class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
          </span>
          Verified report data for the selected store and period
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap gap-3 p-1 bg-base-100 rounded-2xl border border-base-300 shadow-sm">
      <button class="btn btn-sm" (click)="selectedTimeRange.set('today'); fetchAnalytics()">Today</button>
      <button class="btn btn-sm" (click)="selectedTimeRange.set('custom')">Custom</button>
      @if (selectedTimeRange() === 'custom') {
        <input type="date" class="input input-sm" [(ngModel)]="customFrom">
        <input type="date" class="input input-sm" [(ngModel)]="customTo">
        <button class="btn btn-sm" (click)="fetchAnalytics()">Apply dates</button>
      }
      <div class="join join-horizontal">
        <button class="join-item btn btn-sm h-10 px-4 font-bold border-none transition-all" 
                [class.btn-primary]="selectedTimeRange() === '7d'" (click)="selectedTimeRange.set('7d'); fetchAnalytics()">Last 7d</button>
        <button class="join-item btn btn-sm h-10 px-4 font-bold border-none transition-all" 
                [class.btn-primary]="selectedTimeRange() === '30d'" (click)="selectedTimeRange.set('30d'); fetchAnalytics()">30 Days</button>
        <button class="join-item btn btn-sm h-10 px-4 font-bold border-none transition-all" 
                [class.btn-primary]="selectedTimeRange() === '90d'" (click)="selectedTimeRange.set('90d'); fetchAnalytics()">Last 90 days</button>
      </div>
      <div class="divider divider-horizontal mx-0 opa-20"></div>
      <select class="select select-bordered select-sm h-10 rounded-xl border-base-300 font-bold focus:ring-0" 
              (change)="selectedStore.set($any($event.target).value); fetchAnalytics()">
        <option value="all">Global (All Stores)</option>
        @for (store of stores(); track store.id) {
          <option [value]="store.id">{{ store.name }}</option>
        }
      </select>
    </div>
  </header>

  <!-- KPI SUMMARY CARDS -->
  @if (error()) { <div class="alert alert-error">{{ error() }} <button class="btn btn-sm" (click)="fetchAnalytics()">Retry</button></div> }
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    @for (kpi of kpiMetrics(); track kpi.label) {
      <div class="card bg-base-100 border border-base-300/50 shadow-sm hover:shadow-md transition-shadow group rounded-3xl">
        <div class="card-body p-6">
          <div class="flex items-start justify-between">
            <div class="flex flex-col gap-1">
              <span class="text-xs font-black uppercase tracking-widest text-base-content/40">{{ kpi.label }}</span>
              <h2 class="text-3xl font-black text-base-content">{{ kpi.value }}</h2>
            </div>
            <div [class]="'w-12 h-12 rounded-2xl flex items-center justify-center ' + (kpi.color === 'primary' ? 'bg-primary/10 text-primary' : kpi.color === 'secondary' ? 'bg-secondary/10 text-secondary' : kpi.color === 'accent' ? 'bg-accent/10 text-accent' : 'bg-error/10 text-error')">
              <i [class]="kpi.icon + ' text-xl'"></i>
            </div>
          </div>
          <div class="mt-4 pt-4 border-t border-base-200 flex items-center gap-2">
            <span [class]="'text-xs font-bold px-2 py-1 rounded-lg ' + (kpi.trendUp ? 'bg-success/10 text-success' : 'bg-error/10 text-error')">
              <i [class]="kpi.trendUp ? 'fa-solid fa-arrow-trend-up' : 'fa-solid fa-arrow-trend-down'"></i>
              {{ kpi.trend }}
            </span>
            <span class="text-[10px] font-medium text-base-content/30 lowercase">vs last period</span>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- MAIN ANALYTICS GRID -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
    
    <!-- LEFT: Trends & Performance -->
    <div class="lg:col-span-8 space-y-8">
      
      <!-- Revenue Trend Chart -->
      <div class="card bg-base-100 border border-base-300 rounded-[2.5rem] overflow-hidden shadow-sm">
        <div class="card-body p-8 space-y-8">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl font-black text-base-content">Revenue Performance</h3>
              <p class="text-xs font-medium text-base-content/40 mt-1 uppercase tracking-tight">Fulfilled sales by delivery day</p>
            </div>
            <div class="flex items-center gap-6">
               <div class="flex items-center gap-2">
                 <div class="w-3 h-3 rounded-full bg-primary shadow-lg shadow-primary/40"></div>
                 <span class="text-[11px] font-black uppercase">Revenue</span>
               </div>
            </div>
          </div>

          <!-- SVG CHART CONTAINER -->
          <div class="relative h-[250px] w-full pt-10 group mt-4">
             @if (isLoading()) {
               <div class="absolute inset-0 flex items-center justify-center bg-base-100/80 z-10">
                 <span class="loading loading-spinner loading-lg text-primary"></span>
               </div>
             }
             <!-- Grid Lines -->
             <div class="absolute inset-x-0 bottom-0 top-10 flex flex-col justify-between opacity-5">
               <div class="border-t border-base-content w-full"></div>
               <div class="border-t border-base-content w-full"></div>
               <div class="border-t border-base-content w-full"></div>
               <div class="border-t border-base-content w-full"></div>
             </div>

             <!-- Main Path -->
             <svg width="100%" height="100%" viewBox="0 0 1000 200" preserveAspectRatio="none" class="overflow-visible drop-shadow-2xl">
               <defs>
                 <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                   <stop offset="0%" style="stop-color:oklch(var(--p)); stop-opacity:0.2" />
                   <stop offset="100%" style="stop-color:oklch(var(--p)); stop-opacity:0" />
                 </linearGradient>
               </defs>
               <path [attr.d]="getTrendPath()" fill="transparent" stroke="oklch(var(--p))" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
               <path [attr.d]="getTrendPath() + ' L 1000 200 L 0 200 Z'" fill="url(#chartGradient)" />
             </svg>
          </div>

          <!-- X-Axis Labels -->
          <div class="flex justify-between px-2 pt-2 border-t border-base-200">
             @for (sale of salesTrends(); track sale.date; let last = $last, first = $first) {
               @if (first || last) {
                <span class="text-[10px] font-black text-base-content/40 uppercase tracking-widest">{{ sale.date }}</span>
               }
             }
          </div>
        </div>
      </div>

      <!-- Detail Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        <!-- Top Products List -->
        <div class="card bg-base-100 border border-base-300 rounded-[2.5rem] shadow-sm">
          <div class="card-body p-8">
            <div class="flex items-center justify-between mb-8">
              <h3 class="text-xl font-black">Top Velocity Products</h3>
              <button class="btn btn-ghost btn-xs text-primary font-black uppercase tracking-widest p-0">View All</button>
            </div>
            
            <div class="space-y-5">
              @for (prod of topProducts(); track prod.sku; let i = $index) {
                <div class="flex items-center gap-4 p-3 bg-base-200/20 rounded-2xl transition-all hover:bg-base-200/50">
                  <div class="relative">
                    <img [src]="prod.image || 'https://placehold.co/100'" alt="Product preview" class="w-12 h-12 rounded-xl object-cover ring-4 ring-base-100 shadow-sm" />
                    <span class="absolute -top-2 -left-2 w-5 h-5 rounded-lg bg-base-content text-base-100 flex items-center justify-center text-[10px] font-black">
                      #{{ i + 1 }}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4 class="text-xs font-black truncate uppercase tracking-tight">{{ prod.name }}</h4>
                    <p class="text-[10px] font-bold text-base-content/40 mt-0.5 truncate">{{ prod.variant_name }}</p>
                  </div>
                  <div class="text-right shrink-0">
                    <div class="text-sm font-black text-base-content">₹{{ prod.total_revenue.toLocaleString() }}</div>
                    <div class="text-[10px] font-bold text-success mt-0.5 uppercase tracking-widest">{{ prod.total_quantity }} sold</div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>

        <div class="card border border-base-300 bg-base-100 p-6">
          <h3 class="font-bold">Store performance and operational health</h3>
          <p class="text-sm opacity-70">Use Store Performance reports and Needs Action for verified figures. Fleet availability and SLA percentages are not available on this screen.</p>
        </div>
      </div>
    </div>
  </div>
</div>
